import React, { useEffect, useState } from 'react';
import Accordion from 'b2b-ui-components-lib/lib/components/accordion';
import RadioGroup from 'b2b-ui-components-lib/lib/components/radioGroup';
import Input from 'b2b-ui-components-lib/lib/components/input';
import Select from 'b2b-ui-components-lib/lib/components/select';
import TextArea from 'b2b-ui-components-lib/lib/components/textarea';
import CustomModal from 'dedicated/components/customModal/customModal';

import { billingTypeOption, getDefaultValue, accountDetailSchema, billableIdFormKey, BILLING_TYPE_LSI, KEYS_TO_IGNORE_LSI } from './FormControl';

import { fetchCityStateList } from 'common/services/http.service';

import { fetch_BA_Accounts_MultiSite, fetchAccountDetailsByAccountNumber_MultiSite } from 'home/view/manage-feasibility/services';

import { prepareOptionsForDropDown } from 'dedicated/services/summary.service';

import { isFormValid } from 'common/utils/validator';

import { addresses, ADDRESS_TYPE_ENUM, SHORT_CB_DELAY, MULTI_SITE_MODULE_ID, SUMMARY_AUTH_TOKEN, MULTI_SITE_TABLE_EDITED } from 'home/view/manage-feasibility/constants';

import { handleError, numberSort, getFromSessionStorage, filterAccounts, setInSessionStorage } from 'common/utils/app.utils';

import Tooltip from 'common/components/tooltip-component/tooltip-component';

import { TOOLTIP_MESSAGE } from 'home/view/manage-feasibility/constants/multiSite-constants';
import { isNLD_OR_LL, isPreOrder } from 'common/constants/app.constants';

const BillingDetails = ({
  order,
  updateParentForm,
  setFormStatus,
  updateOrder = () => {},
  isOrderConfirmation = false,
  setOrderDetail = () => {},
  setShowLoader = () => {},
  setCustomerAccounts = () => {},
  checkIfPreOrder = () => {}
}) => {
  const [accordionMetadata, setAccordionMetadata] = useState({
    isValidated: false
  });

  const [form, setForm] = useState(getDefaultValue(order));
  const [billingAddress, setBillingAddress] = useState(addresses);

  const [billableIdList, setBillableIdList] = useState([]);
  const [accountNumbers, setAccountNumbers] = useState([]);
  const [showAccWarnPopup, setShowAccWarnPopup] = useState(false);
  const [tempSelectedAccValue, setTempSelectedAccValue] = useState({});

  const onChangeBillingType = (selectedType) => setForm({ ...form, type: selectedType });

  const onAccountNumberChange = (value) => {
    const isSummaryTableEdited = !!getFromSessionStorage(MULTI_SITE_MODULE_ID, MULTI_SITE_TABLE_EDITED);

    if (isSummaryTableEdited && !isNLD_OR_LL()) {
      setTempSelectedAccValue(value);
      setShowAccWarnPopup(true);
    } else {
      setForm({ ...form, accountNumber: { ...form.accountNumber, value, touched: true } });
    }
  };

  const getBAAccounts = async (partyId) => {
    try {
      const summaryAuthToken = getFromSessionStorage(MULTI_SITE_MODULE_ID, SUMMARY_AUTH_TOKEN);

      setShowLoader(true);
      const { res, err } = await fetch_BA_Accounts_MultiSite(partyId, summaryAuthToken);

      if (res) {
        const {
          data: { success = false, result = {} }
        } = res;

        if (success) {
          setShowLoader(false);

          const accounts = result && result.accounts;

          if (Array.isArray(accounts)) {
            setCustomerAccounts(accounts);

            const billableList = prepareOptionsForDropDown(
              accounts.filter((account) => account.billable === true),
              'accountNumber',
              numberSort
            );
            const accountList = prepareOptionsForDropDown(filterAccounts(accounts, partyId), 'parentAccountNumber', numberSort);
            setBillableIdList(billableList);
            setAccountNumbers(accountList);
            if (accountList.length === 1) {
              onAccountNumberChange(accountList[0]);
            }
          }
        }
      } else if (err) {
        const errorMsg = (err && err.response && err.response.data && err.response.data.errorMsg) || DEFAULT_TOAST_MSG;

        setShowLoader(false);
        setToast(true, errorMsg, ERROR_TOAST);

        throw new Error(errorMsg);
      }
    } catch (e) {
      console.error(e);
    }
  };

  const getExistingAddress = (payload) => {
    setTimeout(() => {
      const validAddress = addresses.filter(({ id }) => id === ADDRESS_TYPE_ENUM.NEWADDRESS);
      setForm({ ...form, selected: validAddress[0] });
      setBillingAddress(validAddress);
    }, 10);
  };

  const onDropDownChange = (value, key) => {
    if (value) {
      setForm({
        ...form,
        [key]: { ...form[key], value, touched: true, errors: [] }
      });
      return;
    }
    setForm({
      ...form,
      [key]: {
        ...form[key],
        value,
        touched: true,
        errors: ['This field is mandatory']
      }
    });
  };

  const clearBlliableId = () => {
    setForm({ ...form, billableId: { ...form.billableId, value: null, touched: true } });
  };

  useEffect(() => {
    setForm((prev) => {
      const billableId = { billableId: prev.billableId };
      return { ...getDefaultValue(order), ...billableId };
    });
  }, [order]);

  useEffect(() => {
    const {
      lead: {
        customer: {
          authorizedSignatory: {
            address: { state }
          },
          partyId
        }
      }
    } = order;
    const payload = { contactType: 'BCP', partyId, state };
    getExistingAddress(payload);
    getBAAccounts(partyId);
  }, []);

  useEffect(() => {
    let keyToIgnore = KEYS_TO_IGNORE_LSI;
    if (isFormValid(form, keyToIgnore)) {
      setAccordionMetadata({ isValidated: true });
      updateParentForm({ billingDetails: form });
      setFormStatus({ billingDetails: true });
    } else {
      setAccordionMetadata({ isValidated: false });
      updateParentForm({ billingDetails: null });
      setFormStatus({ billingDetails: false });
    }

    let timeoutId;
    if (isFormValid(form, keyToIgnore)) {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => updateOrder(form, false), SHORT_CB_DELAY);
    }

    return () => {
      clearTimeout(timeoutId);
    };
  }, [form]);

  const handleConfirmAccUpdate = () => {
    setInSessionStorage(MULTI_SITE_MODULE_ID, MULTI_SITE_TABLE_EDITED, false);
    setForm({ ...form, accountNumber: { ...form.accountNumber, value: tempSelectedAccValue, touched: true } });
    setShowAccWarnPopup(false);
    setTempSelectedAccValue({});
  };

  const getBillingTypeOptions = () => {
    if (isPreOrder() || checkIfPreOrder()) {
      return billingTypeOption.filter((item) => item.id !== 'PO');
    }

    return billingTypeOption;
  };

  return (
    <React.Fragment>
      {showAccWarnPopup && (
        <CustomModal
          customPopupTitle="Please note"
          customPopupMsg="Changing the Account number will reset the configurations below."
          submitCustomPopup={handleConfirmAccUpdate}
          closeCustomPopup={() => setShowAccWarnPopup(false)}
        />
      )}
      <Accordion
        title="Billing Details"
        autoScrollOnExpand={false}
        isExpanded={true}
        isValidated={accordionMetadata.isValidated}
        isEditable={accordionMetadata.isEditable}
        onEditClick={() => setAccordionMetadata(({ isEditable }) => ({ ...accordionMetadata, isEditable: !isEditable }))}
      >
        <div className="accordion_body">
          <div className="devider_group">
            <div className="form_area">
              <h6>Billing Level</h6>
              <RadioGroup options={getBillingTypeOptions()} value={form.type} onChange={onChangeBillingType} disabled={isOrderConfirmation} />
            </div>

            <div className="form_area">
              <h6 className="form-sub-title">Choose a Billing Account to get the billing related details or enter the required details manually.</h6>
              <div className="row field_row">
                <div className="flg-lg-6 flg-md-6">
                  <Select
                    label="Account Number"
                    value={form.accountNumber.value}
                    onChange={onAccountNumberChange}
                    options={accountNumbers}
                    placeholder="Account Number"
                    required
                    disabled={isOrderConfirmation}
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="row field_row">
            <div className="flg-lg-6 flg-md-6">
              <div className="field_tooltip">
                <Select
                  label="Billing Payment Type"
                  value={form.paymentType.value}
                  onChange={(value) => {
                    onDropDownChange(value, 'paymentType');
                  }}
                  options={form.paymentType.options}
                  placeholder="Billing Payment Type"
                  required
                  disabled={isOrderConfirmation}
                />
              </div>
            </div>
            <div className="flg-lg-6 flg-md-6">
              <div className="field_tooltip">
                <Select
                  label="Billing Payment Cycle"
                  value={form.paymentCycle.value}
                  onChange={(value) => {
                    onDropDownChange(value, 'paymentCycle');
                  }}
                  options={form.paymentCycle.options}
                  placeholder="Billing Payment Cycle"
                  required
                  disabled={isOrderConfirmation}
                />
              </div>
            </div>
          </div>
        </div>
      </Accordion>
    </React.Fragment>
  );
};

export default BillingDetails;
